import { Calendar, CheckCircle, Home, Info, MessageSquare, Phone, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import StatusPage, { InfoCard } from '../components/StatusPage';
import { intentAPI } from '../services/api';
import { responseData } from '../services/apiResponse';

const STABLECOIN_META = {
  USDC: { symbol: '$', logoPath: '/assets/tokens/usdc.png' },
  USDT: { symbol: '₮', logoPath: '/assets/tokens/usdt.png' },
};

// Format DOB from "YYYYMMDD" or "YYYY-MM-DD" to "May 15, 1992"
const formatDOB = (raw) => {
  if (!raw) return null;
  const clean = raw.replace(/[-/]/g, '');
  if (clean.length !== 8) return raw;
  const year = clean.substring(0, 4);
  const month = clean.substring(4, 6);
  const day = clean.substring(6, 8);
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const Welcome = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get('linkToken') || '';
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // { type, message, data? } or null
  const [confirmed, setConfirmed] = useState(false);
  const [proceeding, setProceeding] = useState(false);

  useEffect(() => {
    loadIntent();
    // loadIntent intentionally re-runs only when the payment request changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, requestId]);

  const loadIntent = async () => {
    try {
      setLoading(true);
      const response = linkToken
        ? await intentAPI.getIntentByLink(linkToken)
        : await intentAPI.getIntent(requestId);
      setIntent(responseData(response));
      if (responseData(response).status === 'completed') {
        setError({
          type: 'completed',
          message: 'This payment request has already been fulfilled. No further action is needed.',
          data: responseData(response),
        });
        return;
      }

      // Check if request is locked by an active payment
      const lock = responseData(response).transaction_lock;
      if (lock && lock.locked) {
        const currentUserId = localStorage.getItem('user_id');
        if (currentUserId && currentUserId === lock.user_id) {
          // Same user — redirect to status page
          localStorage.setItem('remittance_transaction_id', lock.transaction_id);
          localStorage.setItem('request_id', requestId);
          navigate(`/success/${lock.transaction_id}`, { replace: true });
          return;
        }
        // Different user or not logged in — show locked message
        setError({
          type: 'locked',
          message: 'This payment request has already been handled by another sender. No action is required from you.',
        });
        return;
      }

    } catch (err) {
      const apiError = err.response?.data?.error || '';
      const apiDetail = err.response?.data?.detail || '';
      if (apiError === 'LINK_EXPIRED') {
        setError({ type: 'expired', message: 'This payment link is no longer valid. Please ask the receiver to send a new request.' });
      } else if (apiError === 'LINK_NOT_FOUND') {
        setError({ type: 'not_found', message: 'This payment link is invalid or has been removed. Please check the URL or contact the person who sent it.' });
      } else if (apiError === 'REQUEST_INACTIVE') {
        setError({ type: 'inactive', message: apiDetail || 'This payment request is no longer active.' });
      } else {
        setError({ type: 'generic', message: apiDetail || apiError || 'Failed to load payment request' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!confirmed) return;
    setProceeding(true);
    if (localStorage.getItem('token') && localStorage.getItem('user_id')) {
      try {
        await intentAPI.getIntent(requestId);
        navigate(`/review/${requestId}`);
        return;
      } catch {
        // Continue through request-bound authentication for a different account.
      }
    }
    navigate(`/auth/${requestId}${linkToken ? `?linkToken=${encodeURIComponent(linkToken)}` : ''}`);
  };

  const currencyMeta = STABLECOIN_META[intent?.currency] || { symbol: '$', logoPath: null };
  const currencyLogoUrl = currencyMeta.logoPath || null;

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment request...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    const goHome = () => {
      const token = localStorage.getItem('token');
      navigate(token ? '/' : '/login');
    };

    if (error.type === 'completed') {
      const rows = [];
      if (error.data?.receiver_full_legal_name) rows.push({ label: 'Receiver', value: error.data.receiver_full_legal_name });
      if (error.data?.requested_amount_usd) rows.push({ label: 'Amount', value: `${error.data.requested_amount_usd} ${error.data.currency || 'USD'}` });
      rows.push({ label: 'Status', value: 'Completed', valueClass: 'text-green-600' });
      return (
        <StatusPage
          variant="success"
          title="Payment Completed"
          description={error.message}
          actions={[
            { label: 'Home', icon: Home, onClick: goHome },
          ]}
        >
          {rows.length > 0 && <InfoCard rows={rows} />}
        </StatusPage>
      );
    }

    if (error.type === 'expired') {
      return (
        <StatusPage
          variant="warning"
          title="Payment Request Expired"
          description={error.message}
          actions={[{ label: 'Go to Home', icon: Home, onClick: goHome }]}
        />
      );
    }

    if (error.type === 'locked') {
      return (
        <StatusPage
          variant="blocked"
          title="Payment Unavailable"
          description={error.message}
          actions={[{ label: 'Go to Home', icon: Home, onClick: goHome }]}
        />
      );
    }

    if (error.type === 'not_found') {
      return (
        <StatusPage
          variant="neutral"
          title="Link Not Found"
          description={error.message}
          actions={[{ label: 'Go to Home', icon: Home, onClick: goHome }]}
        />
      );
    }

    if (error.type === 'inactive') {
      return (
        <StatusPage
          variant="blocked"
          title="Payment Request Unavailable"
          description={error.message}
          actions={[{ label: 'Go to Home', icon: Home, onClick: goHome }]}
        />
      );
    }

    // Generic fallback
    return (
      <StatusPage
        variant="error"
        title="Something Went Wrong"
        description={error.message}
        actions={[{ label: 'Go to Home', icon: Home, onClick: goHome }]}
      />
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        {/* Title */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            You've received a remittance request
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Your friend or family sent you a remittance request.
            <strong> Koin Remit</strong> is a service provided by <strong>Kivi3</strong> –
            Instant, Secured and Convenient transfers powered by modern infrastructure.
          </p>
        </div>

        {/* Condensed info card */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          {/* Receiver */}
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Receiver</p>
              <p className="font-semibold text-gray-800">{intent.receiver_full_legal_name}</p>
            </div>
          </div>

          {/* Amount */}
          <div className="flex items-center justify-between bg-white rounded-lg p-3">
            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-gray-800">
                  {intent.requested_amount_usd.toFixed(2)} USD
                </span>
                {currencyLogoUrl && (
                  <img
                    src={currencyLogoUrl}
                    alt={`${intent.currency} logo`}
                    className="w-6 h-6 rounded-full object-contain"
                  />
                )}
              </div>
            </div>
            <a href="/howitworks?tab=stablecoins">
              <Info className="w-5 h-5 text-gray-400 hover:text-primary-600 cursor-pointer" />
            </a>
          </div>

          {/* Phone */}
          {intent.receiver_phone && (
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-800">{intent.receiver_phone}</p>
              </div>
            </div>
          )}

          {/* DOB */}
          {intent.receiver_dob && (
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Date of Birth</p>
                <p className="text-sm font-medium text-gray-800">{formatDOB(intent.receiver_dob)}</p>
              </div>
            </div>
          )}

          {/* Purpose */}
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-5 h-5 text-primary-600 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Purpose</p>
              <p className="text-sm font-medium text-gray-800">{intent.reason_for_sending?.replace(/_/g, ' ')}</p>
              {intent.notes && (
                <p className="text-xs text-gray-500 mt-0.5 italic">"{intent.notes}"</p>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-gray-600">
          Please confirm that you want to proceed with this payment.
        </p>

        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm">⚠️ Important Notice</h3>
          <ul className="space-y-1 text-xs text-gray-700">
            <li>• Verify the receiver's identity before proceeding</li>
            <li>• Ensure the payment amount is correct</li>
            <li>• Be aware of potential scams</li>
            <li>• Once confirmed, the payment cannot be reversed</li>
          </ul>
        </div>

        {/* Confirmation checkbox */}
        <div className={`rounded-lg border-2 p-4 transition-all ${
          confirmed
            ? 'border-primary-600 bg-primary-50 shadow-sm'
            : 'border-gray-300 bg-white hover:border-primary-400'
        }`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-6 w-6 flex-shrink-0 cursor-pointer rounded border-2 border-gray-400 text-primary-600 accent-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            />
            <div className="flex-1">
              <p className="font-semibold text-gray-800">I confirm this payment request</p>
              <p className="text-sm text-gray-600 mt-1">
                I have verified the receiver's information and understand that this payment is final.
              </p>
            </div>
          </label>
        </div>

        {confirmed && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
            <p className="text-sm text-green-800">
              Thank you for confirming. You may now proceed to the next step.
            </p>
          </div>
        )}

        {/* CTA buttons */}
        <div className="flex space-x-4 pt-2">
          <button
            onClick={() => window.history.back()}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!confirmed || proceeding}
            className="btn-primary flex-1"
          >
            Continue
          </button>
        </div>

        {/* Regulatory footer */}
        <p className="text-center text-xs text-gray-400 pt-2">
          A service by Kivi3 Technologies Ltd — Licensed by Dubai International Financial Centre.
        </p>
      </div>
    </Layout>
  );
};

export default Welcome;
